import { PRICE_MIN } from '@peach/types/payments/price-limits.js';
import { CLAIM_CATALOGUE_LIMITS, introMaxPrice } from '@peach/types/tutors/claim-page.consts.js';
import type { InviterDto } from '@peach/types/tutors/create-page.dto.js';
import type { BlockSet } from '@/lib/availability/blocks';
import type { InvitePrices } from '@/lib/stores/invite-page.store';

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

/** What the flow calls the inviter: their first name, else their link. */
export const inviterFirst = (inviter: InviterDto) =>
  inviter.name?.trim().split(/\s+/)[0] || inviter.username;

/** The inviter's week as the editor's rows: the same clock times, with every other day closed. */
export function inviterWeekSet(inviter: InviterDto): BlockSet {
  return {
    blocks: inviter.week.map((b, i) => ({ id: `inviter_${i}`, ...b })),
    disabledDays: new Set(
      [0, 1, 2, 3, 4, 5, 6].filter((d) => !inviter.week.some((b) => b.day === d))
    ),
  };
}

/** Their prices as sold, one line each, never converted: "50-minute call, $65". */
export function inviterPriceLines(inviter: InviterDto): string[] {
  const lines: string[] = [];
  if (inviter.intro)
    lines.push(`Intro call, ${inviter.intro.minutes} min, ${usd(inviter.intro.price)}`);
  if (inviter.single) {
    const { minutes, price } = inviter.single;
    lines.push(minutes === 60 ? `An hour, ${usd(price)}` : `${minutes}-minute call, ${usd(price)}`);
  }
  return lines;
}

/**
 * The visitor's prices after "Use these prices": the inviter's, held inside what a new page may
 * charge, with one note for each value the range moved. A missing value keeps the visitor's own.
 */
export function adoptInviterPrices(
  inviter: InviterDto,
  current: InvitePrices
): { prices: InvitePrices; notes: string[] } {
  const { hourPrice, introPrice, introMinutes } = CLAIM_CATALOGUE_LIMITS;
  const notes: string[] = [];
  const hold = (value: number, min: number, max: number, what: string) => {
    const held = Math.min(max, Math.max(min, Math.round(value)));
    if (held < value)
      notes.push(`${what} here is ${usd(max)} at most, so yours is set to ${usd(held)}.`);
    if (held > value)
      notes.push(`${what} here is ${usd(min)} at least, so yours is set to ${usd(held)}.`);
    return held;
  };
  const prices = { ...current };
  if (inviter.intro) {
    prices.introPrice = hold(inviter.intro.price, PRICE_MIN, introPrice.max, 'An intro call');
    const [short, long] = introMinutes;
    prices.introMinutes =
      Math.abs(inviter.intro.minutes - long) < Math.abs(inviter.intro.minutes - short)
        ? long
        : short;
  }
  if (inviter.single) {
    const perHour = (inviter.single.price * 60) / inviter.single.minutes;
    prices.hourPrice = hold(perHour, hourPrice.min, hourPrice.max, 'An hour');
  }
  // The intro is always a discount on the visitor's hour: an inviter's dearer intro comes down.
  const cap = Math.max(PRICE_MIN, introMaxPrice(prices.hourPrice, prices.introMinutes));
  if (inviter.intro && prices.introPrice > cap) {
    prices.introPrice = cap;
    notes.push(`An intro call here is a discount on your hour, so yours is set to ${usd(cap)}.`);
  }
  return { prices, notes };
}

export const samePrices = (a: InvitePrices, b: InvitePrices) =>
  a.introPrice === b.introPrice && a.introMinutes === b.introMinutes && a.hourPrice === b.hourPrice;
